import { createPersistedStore, type PersistedStoreHook } from '@tasama/utils';
import type { BaseStoreState } from '@tasama/utils';

/**
 * Company Type coordinator store — thin coordinator (portal)
 *
 * No user-facing company-type selection form exists in the portal.
 * This store is a data container: useCompanyFormLogic syncs the known
 * company type here so getCompanyDetailsSchema / getFieldRequirements
 * can read it reactively.
 *
 * Validation logic removed — isValid is derived directly from formData.
 */

export interface CompanyTypeFormData {
  companyType: string;
  companyStatus?: string | undefined;
}

interface CompanyTypeState extends BaseStoreState {
  formData: CompanyTypeFormData;
  isValid: boolean;
}

export interface CompanyTypeStore extends CompanyTypeState {
  setCompanyType: (_type: string) => void;
  setCompanyStatus: (_status: string) => void;
  updateFormData: (_data: Partial<CompanyTypeFormData>) => void;
  resetForm: () => void;
  clearStore: () => void;
}

const initialFormData: CompanyTypeFormData = {
  companyType: '',
  companyStatus: undefined,
};

const computeIsValid = (formData: CompanyTypeFormData): boolean => {
  if (!formData.companyType) return false;
  if (formData.companyType === 'pif-owned') return !!formData.companyStatus;
  return true;
};

export const useCompanyTypeStore = createPersistedStore<CompanyTypeState>(
  'company-type',
  {
    formData: initialFormData,
    isValid: false,
  },
  (set, get) => ({
    setCompanyType: (type: string) => {
      const { formData } = get();
      const newFormData = {
        ...formData,
        companyType: type,
        companyStatus:
          type === 'pif-owned' ? formData.companyStatus : undefined,
      };
      set({ formData: newFormData, isValid: computeIsValid(newFormData) });
    },

    setCompanyStatus: (status: string) => {
      const { formData } = get();
      const newFormData = { ...formData, companyStatus: status };
      set({ formData: newFormData, isValid: computeIsValid(newFormData) });
    },

    updateFormData: (data: Partial<CompanyTypeFormData>) => {
      const { formData } = get();
      const newFormData = { ...formData, ...data };
      set({ formData: newFormData, isValid: computeIsValid(newFormData) });
    },

    resetForm: () => set({ formData: initialFormData, isValid: false }),

    clearStore: () => set({ formData: initialFormData, isValid: false }),
  }),
  {
    partialize: (state) => ({
      formData: state.formData,
      isValid: state.isValid,
    }),
  }
) as unknown as PersistedStoreHook<CompanyTypeStore>;

export const useCompanyTypeFormData = () =>
  useCompanyTypeStore((state) => state.formData);
export const useCompanyTypeIsValid = () =>
  useCompanyTypeStore((state) => state.isValid);
// Stub selectors kept for backward-compatibility
export const useCompanyTypeErrors = () =>
  ({}) as Partial<Record<keyof CompanyTypeFormData, string>>;
export const useCompanyTypeIsDirty = () => false;
export const useCompanyTypeLoading = () => false;
